<template>
	<DataTable :value="batchClasses"
		:loading="loading"
		class="p-datatable-sm p-datatable-documents"
		:autoLayout="true"
		columnResizeMode="fit"
		v-model:selection="selection"
		selectionMode="single"
		@row-select="onRowSelect"
		:scrollable="true"
		scrollHeight="flex"
		:rowHover="true"
		:removableSort="true"
		filterDisplay="row"
		v-model:filters="filters"
		@filter="onFilter($event)"
		@sort="onSort($event)"
		:sortField="currentSortField"
		:sortOrder="currentSortOrder"
	>
		<template #loading>
			{{  $t('Squeeze.General.Loading') }}
		</template>
		<template #empty>
			{{  $t('Squeeze.General.NoEntriesFound', { entryName: $t('Squeeze.BatchClasses.BatchClasses') }) }}
		</template>

		<Column field="id" header="ID" filterField="id" :sortable="true" :showFilterMenu="false" style="min-width: 4rem; max-width: 4rem;">
			<template #body="slotProps">
				{{slotProps.data.id}}
			</template>
			<template #filter="{filterModel, filterCallback}">
				<InputText style="min-width: 3rem;" class="p-column-filter filter-field" type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" />
			</template>
		</Column>
		<Column field="name" filterField="name" :header="$t('Squeeze.DocumentClasses.Name')" :sortable="true" :showFilterMenu="false" style="min-width: 10rem;">
			<template #body="slotProps">
				<div class="column-cell" v-tooltip.bottom="slotProps.data.name">
					{{slotProps.data.name}}
				</div>
			</template>
			<template #filter="{filterModel, filterCallback}">
				<InputText style="min-width: 10rem;" class="p-column-filter filter-field" type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" />
			</template>
		</Column>
		<Column field="description" filterField="description" :header="$t('Squeeze.DocumentClasses.Description')" :sortable="true" :showFilterMenu="false" style="min-width: 10rem;">
			<template #body="slotProps">
				<div class="column-cell" v-tooltip.bottom="getTranslatedDescription(slotProps.data)">
					{{ getTranslatedDescription(slotProps.data) }}
				</div>
			</template>
			<template #filter="{filterModel, filterCallback}">
				<InputText style="min-width: 10rem;" class="p-column-filter filter-field" type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" />
			</template>
		</Column>
		<!-- Only active with active FeatureFlag "llmExtraction" -->
		<Column v-if="store.state.featureSet.llmExtraction" field="llmActive" :header="$t('Squeeze.DocumentClasses.LLMActive')" headerClass="header-center" :showFilterMenu="false" style="min-width: 6rem; max-width: 6rem;">
			<template #body="slotProps">
				<Checkbox :modelValue="slotProps.data.llmActive === true" :binary="true" :disabled="true" />
			</template>
		</Column>
		<!-- Delete & Edit -->
		<Column style="min-width: 3rem; max-width: 3rem; text-align: right;">
			<template #body="slotProps">
				<Button v-tooltip.bottom="$t('Squeeze.Validation.Buttons.Delete')" icon="mdi mdi-delete-outline" class="p-button-lg p-button-rounded p-button-danger p-button-text" @click="openDeleteDialog(slotProps.data)" />
			</template>
		</Column>

		<template #footer>
			<Button :label="$t('Squeeze.General.NewEntry')" type="button" icon="mdi mdi-plus" class="p-button" @click="onNewEntryClick" />
			<Button :label="$t('Squeeze.System.Translations')" type="button" icon="mdi mdi-translate" class="p-button p-ml-2" @click="openTranslations" />
			<Button v-if="showBtnClearFilter" :label="$t('Squeeze.General.ClearFilters')" type="button" icon="mdi mdi-filter-off-outline" class="p-button p-ml-2" @click="clearFilters"/>
			<Button v-tooltip.bottom="$t('Squeeze.Validation.Buttons.ResetTableSort')" type="button" icon="mdi mdi-sort-variant-remove" class="p-button p-button-text p-ml-2" @click="resetTableSettings" />
		</template>
	</DataTable>
</template>

<script lang="ts">
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import InputText from "primevue/inputtext";
import Checkbox from "primevue/checkbox";
import {defineComponent, onMounted, ref} from "vue";
import {BatchClass} from "@dex/squeeze-client-ts";
import Tooltip from "primevue/tooltip";
import {DocumentFilterObject} from "@/apps/squeeze/interfaces/DocumentSearch";
import {FilterMatchMode} from "primevue/api";
import {useI18n} from "vue-i18n";
import {useToast} from "primevue/usetoast";
import {useRoute} from "vue-router";
import {TableSettings} from "@/util/TableSettings";
import {getTranslatedDescription} from "@/util/TranslationHelper";
import {ClientManager} from "@/singletons/ClientManager";
import {useSqueezeStore} from "@/apps/squeeze/store";

export default defineComponent({
	name: "BatchClassList",
	components: {
		DataTable, Column, InputText, Checkbox,
	},
	props: {
		batchClasses: {
			type: Array,
		},
		loading: {
			type: Boolean,
			default: false,
		},
	},
	directives: {
		'tooltip': Tooltip,
	},
	emits: [
		"onClickDelete", "onEntrySelect", "openTranslations",
	],
	setup(props, {emit}) {
		const {t} = useI18n();
		const toast = useToast();
		const store = useSqueezeStore();
		const route = useRoute();

		/** User API for persisting table settings */
		const userApi = ClientManager.getInstance().squeeze.user;

		/** Current sort field */
		const currentSortField = ref<string | undefined>(undefined);

		/** Current sort order */
		const currentSortOrder = ref<number>(0);

		/** Currently-Selected row */
		const selection = ref(null);

		/** Filters of list (Currently not activated) */
		const filters = ref<DocumentFilterObject>({});

		/** Should the clear filter button be shown in the table? */
		const showBtnClearFilter = ref<boolean>(false);

		/** Init filters */
		const initFilters = () => {
			filters.value = {
				"id": {value: null, matchMode: FilterMatchMode.EQUALS},
				"name": {value: null, matchMode: FilterMatchMode.CONTAINS},
				"description": {value: null, matchMode: FilterMatchMode.CONTAINS},
			};
		}

		/**
		 * Opens the Delete-Dialog
		 * @param row The row to delete
		 */
		const openDeleteDialog = (row: BatchClass) => {
			emit("onClickDelete", row);
		}

		/** Triggered when the New Entry Button is clicked */
		const onNewEntryClick = () => {
			emit("onEntrySelect", 0);
		}

		/** Triggered when a row is selected */
		const onRowSelect = (event: any) => {
			emit("onEntrySelect", event.data.id);
		}

		/**
		 * Triggered when a filter has been entered
		 * @param event
		 */
		const onFilter = (event: any) => {
			if (event.filteredValue.length < props.batchClasses!.length) {
				showBtnClearFilter.value = true;
			} else {
				showBtnClearFilter.value = false;
			}
		}

		/** Clear filters */
		const clearFilters = () => {
			initFilters();
		}

		/** Opens the translations page for all batch class translation keys */
		const openTranslations = () => {
			emit("openTranslations");
		}

		initFilters();

		/** Triggered on sort a column */
		const onSort = (event: { sortField: string; sortOrder: number }) => {
			currentSortField.value = event.sortField;
			currentSortOrder.value = event.sortOrder;
			TableSettings.saveTableSubSetting(t, toast, store, userApi, route.name as string, 'sort', '', { field: event.sortField, order: event.sortOrder }, v => !v || v.order === 0);
		}

		/** Reset sort settings */
		const resetTableSettings = () => {
			currentSortField.value = undefined;
			currentSortOrder.value = 0;
			TableSettings.saveTableSubSetting(t, toast, store, userApi, route.name as string, 'sort', '', { field: '', order: 0 }, v => !v || v.order === 0);
		}

		onMounted(() => {
			const savedSort = TableSettings.getTableSubSetting<{ field: string; order: number }>(store, route.name as string, 'sort', '');
			if (savedSort) {
				currentSortField.value = savedSort.field;
				currentSortOrder.value = savedSort.order;
			}
		});

		return {
			getTranslatedDescription,
			store, DataTable, Column, filters, showBtnClearFilter, initFilters, openDeleteDialog, onRowSelect, selection, onNewEntryClick, onFilter, clearFilters, openTranslations,
			currentSortField, currentSortOrder, onSort, resetTableSettings,
		}
	},
});
</script>

<style lang="scss" scoped>

// Table header
.p-datatable.p-datatable-documents ::v-deep(.p-datatable-thead tr th) {
	background-color: var(--dex-primary-light-color);
	padding: 0.6rem 0.6rem 0.2rem 0.3rem;
	border-bottom-color: var(--dex-primary-light-color);
	color: var(--dex-text-color);
}

.p-datatable.p-datatable-documents ::v-deep(.p-datatable-thead tr th:first-child) {
	padding: 0.6rem 0.6rem 0.2rem 0.6rem;
}

.p-datatable.p-datatable-documents ::v-deep(.p-datatable-thead .p-sortable-column-icon) {
	color: var(--dex-text-color);

	&:hover {
		color: var(--dex-text-color);
	}
}

.p-datatable.p-datatable-documents ::v-deep(.p-datatable-thead .p-inputtext.filter-field) {
	padding: 0.4rem;
	background-color: transparent;
	color: var(--dex-text-color);
	border-radius: 0;
	border-color: var(--filter-field-color);
	border-style: solid;
	border-width: 0.063rem;

	&:focus {
		border-color: rgba(255, 255, 255, 0.8);
	}
}

</style>
